/**
 * EXT-DEFERRED-PAYOUT(process-branch) — everything the client needs to know about the
 * deferred-payment branches that process v2 adds to default-booking and
 * default-purchase (see server/extensions/ext-deferred-payout/install/*.edn).
 *
 * The host template's process modules stay STOCK. `applyDeferredPayoutBranch` extends
 * them at the one place they are aggregated (src/transactions/transaction.js PROCESSES):
 * new transitions are merged into the process's `transitions` map, slotted into the
 * client graph pointing at the SAME UI states as their non-deferred twins (so every
 * state-keyed UI — stateData conditionals, ActivityFeed microcopy, InboxPage — works
 * unchanged, with zero new translations), and the is* helper predicates are wrapped.
 *
 * Naming contract: every deferred transition is exactly `${baseTransition}-deferred`.
 * That contract is what lets `resolveDeferredTwinTransition` swap action-button /
 * dispute dispatches to the twin with a single hook in TransactionPage.stateData.js.
 */

const DEFERRED_SUFFIX = '-deferred';

const enabled = () => process.env.REACT_APP_DEFERRED_PAYOUT_ENABLED === 'true';

/**
 * A transition name is on the deferred branch when it carries the -deferred suffix.
 */
export const isDeferredTransition = transition =>
  !!transition && transition.endsWith(DEFERRED_SUFFIX);

/**
 * A transaction is on the deferred branch when its latest transition carries the
 * -deferred suffix. Reliable wherever deferred-specific dispatching is needed: by the
 * time the branches converge (reviews), no deferred-specific transition remains.
 */
export const isDeferredTx = tx => isDeferredTransition(tx?.attributes?.lastTransition);

// ================ Branch specs ================ //
// graphOn: how each new transition slots into the CLIENT graph — fromState → { transition: toState }.
// The client states are the stock ones on purpose (see the file comment).

const BOOKING_BRANCH = {
  transitions: {
    REQUEST_PAYMENT_DEFERRED: 'transition/request-payment-deferred',
    EXPIRE_PAYMENT_DEFERRED: 'transition/expire-payment-deferred',
    CONFIRM_PAYMENT_DEFERRED: 'transition/confirm-payment-deferred',
    ACCEPT_DEFERRED: 'transition/accept-deferred',
    OPERATOR_ACCEPT_DEFERRED: 'transition/operator-accept-deferred',
    DECLINE_DEFERRED: 'transition/decline-deferred',
    OPERATOR_DECLINE_DEFERRED: 'transition/operator-decline-deferred',
    EXPIRE_DEFERRED: 'transition/expire-deferred',
    COMPLETE_DEFERRED: 'transition/complete-deferred',
    OPERATOR_COMPLETE_DEFERRED: 'transition/operator-complete-deferred',
    CANCEL_DEFERRED: 'transition/cancel-deferred',
  },
  graphOn: {
    initial: { 'transition/request-payment-deferred': 'pending-payment' },
    'pending-payment': {
      'transition/expire-payment-deferred': 'payment-expired',
      'transition/confirm-payment-deferred': 'preauthorized',
    },
    preauthorized: {
      'transition/accept-deferred': 'accepted',
      'transition/operator-accept-deferred': 'accepted',
      'transition/decline-deferred': 'declined',
      'transition/operator-decline-deferred': 'declined',
      'transition/expire-deferred': 'expired',
    },
    accepted: {
      'transition/complete-deferred': 'delivered',
      'transition/operator-complete-deferred': 'delivered',
      'transition/cancel-deferred': 'canceled',
    },
  },
  privileged: ['transition/request-payment-deferred'],
  relevantPast: [
    'transition/confirm-payment-deferred',
    'transition/accept-deferred',
    'transition/operator-accept-deferred',
    'transition/decline-deferred',
    'transition/operator-decline-deferred',
    'transition/expire-deferred',
    'transition/complete-deferred',
    'transition/operator-complete-deferred',
    'transition/cancel-deferred',
  ],
  completed: ['transition/complete-deferred', 'transition/operator-complete-deferred'],
  // No stripe-refund on the branch — empavision's sweep refunds the external charge
  // when it sees the transaction in one of these terminal states.
  refunded: [
    'transition/expire-payment-deferred',
    'transition/expire-deferred',
    'transition/cancel-deferred',
    'transition/decline-deferred',
    'transition/operator-decline-deferred',
  ],
};

const PURCHASE_BRANCH = {
  transitions: {
    REQUEST_PAYMENT_DEFERRED: 'transition/request-payment-deferred',
    EXPIRE_PAYMENT_DEFERRED: 'transition/expire-payment-deferred',
    CONFIRM_PAYMENT_DEFERRED: 'transition/confirm-payment-deferred',
    MARK_DELIVERED_DEFERRED: 'transition/mark-delivered-deferred',
    OPERATOR_MARK_DELIVERED_DEFERRED: 'transition/operator-mark-delivered-deferred',
    MARK_RECEIVED_FROM_PURCHASED_DEFERRED: 'transition/mark-received-from-purchased-deferred',
    CANCEL_DEFERRED: 'transition/cancel-deferred',
    AUTO_CANCEL_DEFERRED: 'transition/auto-cancel-deferred',
    MARK_RECEIVED_DEFERRED: 'transition/mark-received-deferred',
    AUTO_MARK_RECEIVED_DEFERRED: 'transition/auto-mark-received-deferred',
    DISPUTE_DEFERRED: 'transition/dispute-deferred',
    OPERATOR_DISPUTE_DEFERRED: 'transition/operator-dispute-deferred',
    MARK_RECEIVED_FROM_DISPUTED_DEFERRED: 'transition/mark-received-from-disputed-deferred',
    CANCEL_FROM_DISPUTED_DEFERRED: 'transition/cancel-from-disputed-deferred',
    AUTO_CANCEL_FROM_DISPUTED_DEFERRED: 'transition/auto-cancel-from-disputed-deferred',
  },
  graphOn: {
    initial: { 'transition/request-payment-deferred': 'pending-payment' },
    'pending-payment': {
      'transition/expire-payment-deferred': 'payment-expired',
      'transition/confirm-payment-deferred': 'purchased',
    },
    purchased: {
      'transition/mark-delivered-deferred': 'delivered',
      'transition/operator-mark-delivered-deferred': 'delivered',
      'transition/mark-received-from-purchased-deferred': 'received',
      'transition/auto-cancel-deferred': 'canceled',
      'transition/cancel-deferred': 'canceled',
    },
    delivered: {
      'transition/mark-received-deferred': 'received',
      'transition/auto-mark-received-deferred': 'received',
      'transition/dispute-deferred': 'disputed',
      'transition/operator-dispute-deferred': 'disputed',
    },
    disputed: {
      'transition/auto-cancel-from-disputed-deferred': 'canceled',
      'transition/cancel-from-disputed-deferred': 'canceled',
      'transition/mark-received-from-disputed-deferred': 'received',
    },
  },
  privileged: ['transition/request-payment-deferred'],
  relevantPast: [
    'transition/confirm-payment-deferred',
    'transition/auto-cancel-deferred',
    'transition/cancel-deferred',
    'transition/mark-delivered-deferred',
    'transition/operator-mark-delivered-deferred',
    'transition/dispute-deferred',
    'transition/operator-dispute-deferred',
    'transition/auto-cancel-from-disputed-deferred',
    'transition/cancel-from-disputed-deferred',
  ],
  completed: [],
  refunded: [
    'transition/expire-payment-deferred',
    'transition/cancel-deferred',
    'transition/auto-cancel-deferred',
    'transition/auto-cancel-from-disputed-deferred',
    'transition/cancel-from-disputed-deferred',
  ],
};

const BRANCHES = {
  'default-booking': BOOKING_BRANCH,
  'default-purchase': PURCHASE_BRANCH,
};

// ================ Process extension ================ //

const orBase = (baseFn, extraList) => transition =>
  extraList.includes(transition) || baseFn(transition);

const extendProcessModule = (processModule, branch) => {
  const transitions = { ...processModule.transitions, ...branch.transitions };

  const states = { ...processModule.graph.states };
  Object.entries(branch.graphOn).forEach(([stateName, on]) => {
    states[stateName] = {
      ...states[stateName],
      on: { ...(states[stateName]?.on || {}), ...on },
    };
  });
  const graph = { ...processModule.graph, states };

  return {
    ...processModule,
    transitions,
    graph,
    isPrivileged: orBase(processModule.isPrivileged, branch.privileged),
    isRelevantPastTransition: orBase(processModule.isRelevantPastTransition, branch.relevantPast),
    isCompleted: orBase(processModule.isCompleted, branch.completed),
    isRefunded: orBase(processModule.isRefunded, branch.refunded),
  };
};

/**
 * Extend the template's PROCESSES entries (src/transactions/transaction.js) in place.
 * Inert unless REACT_APP_DEFERRED_PAYOUT_ENABLED=true. The single client-side hook for
 * the whole process layer.
 *
 * @param {Array} processes the PROCESSES array ({ name, alias, process, unitTypes })
 * @returns {Array} the same array, entries extended
 */
export const applyDeferredPayoutBranch = processes => {
  if (!enabled()) return processes;
  processes.forEach((entry, i) => {
    const branch = BRANCHES[entry.name];
    if (branch) {
      processes[i] = { ...entry, process: extendProcessModule(entry.process, branch) };
    }
  });
  return processes;
};

/**
 * For a deferred transaction, swap an action-dispatch transition for its -deferred twin
 * (a deferred transaction sits in the parallel *-deferred server states, so the stock
 * transition would be rejected). Falls back to the base name when the process has no
 * twin (reviews etc. — the branches have converged) or the transaction isn't deferred.
 * Translation keys should keep deriving from the BASE name so no new microcopy is needed.
 *
 * @param {Object} process the (extended) process from getProcess
 * @param {Object} transaction transaction entity
 * @param {String} transitionName the stock transition the UI would dispatch
 * @returns {String} the transition to actually dispatch
 */
export const resolveDeferredTwinTransition = (process, transaction, transitionName) => {
  if (!transitionName || !isDeferredTx(transaction)) return transitionName;
  const twin = `${transitionName}${DEFERRED_SUFFIX}`;
  const hasTwin = Object.values(process?.transitions || {}).includes(twin);
  return hasTwin ? twin : transitionName;
};
